<script lang="ts">
	import type { MatchMarkerDto } from '$mapapp/types/matchMarkerDto'
	import { MatchType } from '$lib/mmpv/types/substitutes'
	import { t } from '$i18n/i18n'
	import { createEventDispatcher, onMount } from 'svelte'
	import iconKM from '@/images/kmIcon.svg'
	import iconTransporter from '@/images/transporterIcon.svg'
	import iconAttention from '@/images/attentionIcon.svg'
	import Tooltip from '../../atoms/Tooltip.svelte'
	import PlatformRequestMailInfo from '$mapapp/MapApp/SlideIn/Views/blocks/PlatformRequestMailInfo.svelte'

	const dispatch = createEventDispatcher()
	export let matchData: MatchMarkerDto
	export let referenceLotId: string | undefined = undefined

	let matchType: MatchType = MatchType.MATCH
	let backgroundColor: string = 'white'
	let refreshKey: number = 0

	function onMatchDataChange() {
		setMatchType()
		setBackgroundColor()
	}

	function setBackgroundColor() {
		switch (matchType) {
			case MatchType.RECOMMENDATION:
			case MatchType.SUBSTITUTE:
				backgroundColor = '#FFF6EE'
				break
			case MatchType.MATCH:
				backgroundColor = 'white'
		}
	}

	function setMatchType() {
		if (matchData.isSubstituteMatch) {
			matchType = MatchType.SUBSTITUTE
		} else if (matchData.isConditionalMatch) {
			matchType = MatchType.RECOMMENDATION
		} else {
			matchType = MatchType.MATCH
		}
	}

	function triggerRouteCalculating(objectId: string) {
		dispatch('triggerRouting', { endMarkerId: objectId })
	}

	function refreshMailInfo(event: Event) {
		const e = event as CustomEvent<{ lotId: string; popId: string }>
		// FIXME (global): Harden lotId and popId type
		// HINT: Use weak comparison since this event is propagated from sirius for legacy map
		if (e.detail.lotId == referenceLotId && e.detail.popId == matchData.popId) {
			refreshKey += 1
		}
	}

	$: if (matchData) onMatchDataChange()

	onMount(() => {
		window.addEventListener('platformRequestClosed', refreshMailInfo)

		return () => {
			window.removeEventListener('platformRequestClosed', refreshMailInfo)
		}
	})
</script>

<tr class="match-row" style="--backgroundColor: {backgroundColor}">
	<td colspan="4">
		<div>
			{#if matchType === MatchType.SUBSTITUTE || matchType === MatchType.RECOMMENDATION}
				<span class="match-row__header">
					{$t(`UI.Map.MatchingTableRow.Header.${MatchType[matchType]}`)}
				</span>
			{/if}
			<div class="match-row__content">
				<button
					class="match-row__title-link"
					on:click={() => triggerRouteCalculating(matchData.objectId)}>{matchData.label}</button
				>
				{#if matchData.popId}
					<PlatformRequestMailInfo
						popId={matchData.popId}
						lotId={referenceLotId}
						refreshToken={refreshKey}
					/>
				{/if}
			</div>
		</div>
	</td>
</tr>

<tr class="sub-match-row" style="--backgroundColor: {backgroundColor}">
	<td>
		{#if matchData.price}
			<div style="text-align: left">
				{matchData.price}
			</div>
		{:else}
			<div style="overflow: auto;">{$t('ProfilePrice.Price.isEmpty')}</div>
		{/if}
	</td>
	<td class="sub-match-row__transport-cell">
		<div class="match-data-padding-default" style="max-height: 15px">
			<!--eslint-disable-next-line svelte/no-at-html-tags Legacy and just to render &nbsp; from BE -->
			{@html matchData.transportCostDaily ? matchData.transportCostDaily : matchData.transportCost}
		</div>
		<Tooltip
			ttipIconImage={matchData.transportCostDaily ? iconTransporter : iconKM}
			ttipIconHeight="18"
			ttipIconWidth="18"
			msg={matchData.transportCostDaily
				? matchData.transportCostInfo
				: $t('Matching.alternateTransportCost.tooltip')}
			isHtml
		></Tooltip>
	</td>
	<td>
		<div>
			{matchData.totalCostPerTon}
		</div>
	</td>
	{#if matchData.showEconomyInfo}
		<td class="economy-info">
			{#if matchData.economy}
				<div>
					<!--eslint-disable-next-line svelte/no-at-html-tags Legacy and just to render &nbsp; from BE -->
					{@html matchData.economy}
				</div>
			{:else}
				<div
					class:match-data-padding={matchData.price}
					style="padding-top: .3rem;padding-right: 1.9rem"
				>
					&mdash;
				</div>
				{#if matchData.price}
					<div class="match-data-padding-default">
						<Tooltip
							msg={$t('Matching.saving.tooltip') + '<br><br>' + matchData.calculationPath}
							isHtml
							ttipIconImage={iconAttention}
							ttipIconHeight="18px"
							ttipIconWidth="18px"
						></Tooltip>
					</div>
				{/if}
			{/if}
		</td>
	{/if}
</tr>

<style lang="scss">
	@import '$styles/global';

	.match-row {
		background-color: var(--backgroundColor);

		& > td > div {
			@include flex-col(0.2rem, flex-start);

			padding: 0.2rem 0 0.5rem 0.3rem;
		}

		.match-row__content {
			@include flex-row(0.2rem, flex-start, space-between);

			width: 100%;
		}

		.match-row__title-link {
			all: unset;
			font-family: Roboto, sans-serif;
			font-style: normal;
			font-weight: 500;
			font-size: 12px;
			line-height: 14px;
			color: #557eba;
			cursor: pointer;
			max-width: 300px;
			overflow: hidden;
			display: -webkit-box;
			line-clamp: 3;
			-webkit-line-clamp: 3;
			-webkit-box-orient: vertical;
		}

		.match-row__header {
			padding-top: 0.2rem;

			@include roboto-font(0.875rem, 400, 0.725rem, #fda62a);
		}
	}

	.sub-match-row {
		background-color: var(--backgroundColor);

		& > td {
			padding-bottom: 0.2rem;

			& > div {
				text-align: right;
			}
		}

		& > td:first-child {
			& > div {
				text-align: left;
			}
		}

		& > td.sub-match-row__transport-cell {
			display: flex;
			float: right;
		}

		& > td.economy-info {
			display: flex;
			float: right;
			padding-bottom: 0.45rem;

			& > div {
				padding-right: 0.5rem;
			}
		}

		& > td > div {
			@include roboto-font(0.875rem, 500, 0.725rem, black);

			padding-left: 0.3rem;
		}

		.match-data-padding {
			padding-right: 0.2rem !important;
		}

		.match-data-padding-default {
			padding-right: 0.3rem !important;
		}
	}

	th,
	td,
	tr {
		border: none;
		padding: 0 0.5rem 0 0.5rem;
		align-items: center;
	}

	a {
		@include roboto-font(0.875rem, 500, 0.7625rem, #557eba);

		cursor: pointer;
		padding-top: 0.3rem;
	}
</style>
